'use client';

import {
  fetchCustomerMeasurement,
  saveCustomerMeasurement,
  saveCustomerMeasurementFeedback,
  type CustomerMeasurementData,
  type CustomerMeasurementFeedback,
  type CustomerMeasurementSize,
  type SaveCustomerMeasurementPayload,
} from '@/api/customerMeasurement';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { useCallback, useEffect } from 'react';
import {
  getMeasurementFeedbackSubmitted,
  replaceMeasurementSession,
  saveFaceMeasurementSession,
  savePdMeasurementSession,
  setMeasurementFeedbackSubmitted,
  useMeasurementFeedbackSubmitted,
} from './session.client';
import type { FaceMeasurementRecord, PdMeasurementRecord } from './types';

export const GUEST_PD_MEASUREMENT_STORAGE_KEY = 'zeelool:measurement:guest-pd:v1';
export const GUEST_FACE_WIDTH_MEASUREMENT_STORAGE_KEY = 'zeelool:measurement:guest-face-width:v1';
export const MEASUREMENT_FEEDBACK_STORAGE_KEY = 'zeelool:measurement:feedback:v1';

export type MeasurementStorage = Pick<Storage, 'getItem' | 'removeItem' | 'setItem'>;

interface CachedPdMeasurement {
  pd: number;
  measured_at: number;
}

interface CachedFaceWidthMeasurement {
  face_width: number;
  recommended_sizes: CustomerMeasurementSize[];
  measured_at: number;
}

interface CachedMeasurementFeedback {
  feedback: CustomerMeasurementFeedback;
  pending: boolean;
  owner_customer_id: string | null;
}

interface CachedMeasurementState {
  pd: CachedPdMeasurement | null;
  faceWidth: CachedFaceWidthMeasurement | null;
  feedback: CachedMeasurementFeedback | null;
}

interface MeasurementPersistenceApi {
  fetchMeasurement: typeof fetchCustomerMeasurement;
  saveMeasurement: typeof saveCustomerMeasurement;
  saveFeedback: typeof saveCustomerMeasurementFeedback;
}

const DEFAULT_API: MeasurementPersistenceApi = {
  fetchMeasurement: fetchCustomerMeasurement,
  saveMeasurement: saveCustomerMeasurement,
  saveFeedback: saveCustomerMeasurementFeedback,
};
let persistenceMutationRevision = 0;
let synchronizationGeneration = 0;

const CUSTOMER_SIZE_BY_NORMALIZED_VALUE: Record<string, CustomerMeasurementSize> = {
  narrow: 'narrow',
  medium: 'medium',
  wide: 'wide',
  extrawide: 'extra_wide',
};

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isUnixSeconds(value: unknown): value is number {
  return isFiniteNumber(value) && Number.isInteger(value) && value > 0;
}

function isPdValue(value: unknown): value is number {
  return (
    isFiniteNumber(value) &&
    value >= 54 &&
    value <= 78 &&
    Math.abs(value * 2 - Math.round(value * 2)) < 1e-8
  );
}

function isFaceWidthValue(value: unknown): value is number {
  return (
    isFiniteNumber(value) &&
    value >= 110 &&
    value <= 180 &&
    Math.abs(value * 10 - Math.round(value * 10)) < 1e-8
  );
}

function normalizeCustomerSize(value: string): CustomerMeasurementSize | undefined {
  return CUSTOMER_SIZE_BY_NORMALIZED_VALUE[
    value
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, '')
  ];
}

function normalizeCustomerSizes(values: string[]): CustomerMeasurementSize[] {
  const sizes = values
    .map(normalizeCustomerSize)
    .filter((value): value is CustomerMeasurementSize => Boolean(value));
  return Array.from(new Set(sizes)).slice(0, 2);
}

function readJson(storage: MeasurementStorage, key: string): unknown {
  try {
    const value = storage.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
}

function writeJson(storage: MeasurementStorage, key: string, value: unknown): boolean {
  try {
    storage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

function removeStorageValue(storage: MeasurementStorage, key: string) {
  try {
    storage.removeItem(key);
  } catch {
    // 浏览器禁用存储时保留当前标签页内存状态。
  }
}

function getResponseCode(value: unknown): number | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  if ('code' in value && typeof value.code === 'number') return value.code;
  if (
    'response' in value &&
    typeof value.response === 'object' &&
    value.response !== null &&
    'status' in value.response &&
    typeof value.response.status === 'number'
  ) {
    return value.response.status;
  }
  return undefined;
}

function parseCachedPd(value: unknown): CachedPdMeasurement | null {
  if (
    typeof value !== 'object' ||
    value === null ||
    !('pd' in value) ||
    !isPdValue(value.pd) ||
    !('measured_at' in value) ||
    !isUnixSeconds(value.measured_at)
  ) {
    return null;
  }

  return { pd: value.pd, measured_at: value.measured_at };
}

function parseCachedFaceWidth(value: unknown): CachedFaceWidthMeasurement | null {
  if (
    typeof value !== 'object' ||
    value === null ||
    !('face_width' in value) ||
    !isFaceWidthValue(value.face_width) ||
    !('recommended_sizes' in value) ||
    !Array.isArray(value.recommended_sizes) ||
    !value.recommended_sizes.every((size) => typeof size === 'string') ||
    !('measured_at' in value) ||
    !isUnixSeconds(value.measured_at)
  ) {
    return null;
  }

  const recommendedSizes = normalizeCustomerSizes(value.recommended_sizes);
  if (recommendedSizes.length === 0) return null;

  return {
    face_width: value.face_width,
    recommended_sizes: recommendedSizes,
    measured_at: value.measured_at,
  };
}

function parseCachedFeedback(value: unknown): CachedMeasurementFeedback | null {
  if (
    typeof value !== 'object' ||
    value === null ||
    !('feedback' in value) ||
    !['positive', 'negative'].includes(String(value.feedback)) ||
    !('pending' in value) ||
    typeof value.pending !== 'boolean' ||
    !('owner_customer_id' in value) ||
    (value.owner_customer_id !== null && typeof value.owner_customer_id !== 'string')
  ) {
    return null;
  }

  return {
    feedback: value.feedback as CustomerMeasurementFeedback,
    pending: value.pending,
    owner_customer_id: value.owner_customer_id as string | null,
  };
}

export function readCachedMeasurementState(storage: MeasurementStorage): CachedMeasurementState {
  return {
    pd: parseCachedPd(readJson(storage, GUEST_PD_MEASUREMENT_STORAGE_KEY)),
    faceWidth: parseCachedFaceWidth(readJson(storage, GUEST_FACE_WIDTH_MEASUREMENT_STORAGE_KEY)),
    feedback: parseCachedFeedback(readJson(storage, MEASUREMENT_FEEDBACK_STORAGE_KEY)),
  };
}

function toMeasuredAtIso(measuredAt: number) {
  return new Date(measuredAt * 1000).toISOString();
}

function toCachedPdRecord(record: CachedPdMeasurement): PdMeasurementRecord {
  return {
    recordId: `guest-pd-${record.measured_at}`,
    measuredAt: toMeasuredAtIso(record.measured_at),
    entrySource: 'guest-cache',
    sourceMode: 'card',
    onlinePdMm: record.pd,
    sdkVersion: 'persisted',
    confidencePercent: 0,
  };
}

function toCachedFaceWidthRecord(record: CachedFaceWidthMeasurement): FaceMeasurementRecord {
  return {
    recordId: `guest-face-width-${record.measured_at}`,
    measuredAt: toMeasuredAtIso(record.measured_at),
    entrySource: 'guest-cache',
    sourceMode: 'card',
    faceWidthMm: record.face_width,
    recommendedFrameSizes: record.recommended_sizes,
    sdkVersion: 'persisted',
    confidencePercent: 0,
  };
}

function toAccountPdRecord(data: CustomerMeasurementData): PdMeasurementRecord | null {
  if (!isPdValue(data.pd) || !isUnixSeconds(data.pd_measured_at)) return null;

  return {
    recordId: `account-pd-${data.pd_measured_at}`,
    measuredAt: toMeasuredAtIso(data.pd_measured_at),
    entrySource: 'account',
    sourceMode: 'card',
    onlinePdMm: data.pd,
    sdkVersion: 'persisted',
    confidencePercent: 0,
  };
}

function toAccountFaceWidthRecord(data: CustomerMeasurementData): FaceMeasurementRecord | null {
  if (!isFaceWidthValue(data.face_width) || !isUnixSeconds(data.face_width_measured_at)) {
    return null;
  }

  const recommendedSizes = normalizeCustomerSizes(data.recommended_sizes);
  if (recommendedSizes.length === 0) return null;

  return {
    recordId: `account-face-width-${data.face_width_measured_at}`,
    measuredAt: toMeasuredAtIso(data.face_width_measured_at),
    entrySource: 'account',
    sourceMode: 'card',
    faceWidthMm: data.face_width,
    recommendedFrameSizes: recommendedSizes,
    sdkVersion: 'persisted',
    confidencePercent: 0,
  };
}

function replaceSessionFromCache(cache: CachedMeasurementState) {
  replaceMeasurementSession({
    faceWidth: cache.faceWidth ? toCachedFaceWidthRecord(cache.faceWidth) : null,
    pd: cache.pd ? toCachedPdRecord(cache.pd) : null,
    feedbackSubmitted: Boolean(cache.feedback),
  });
}

function replaceSessionFromAccount(
  data: CustomerMeasurementData,
  fallbackCache: CachedMeasurementState,
  preserveCurrentFeedback = false,
) {
  replaceMeasurementSession({
    faceWidth:
      toAccountFaceWidthRecord(data) ??
      (fallbackCache.faceWidth ? toCachedFaceWidthRecord(fallbackCache.faceWidth) : null),
    pd: toAccountPdRecord(data) ?? (fallbackCache.pd ? toCachedPdRecord(fallbackCache.pd) : null),
    feedbackSubmitted:
      data.feedback_submitted ||
      Boolean(fallbackCache.feedback) ||
      (preserveCurrentFeedback && getMeasurementFeedbackSubmitted()),
  });
}

function getBrowserStorage(): MeasurementStorage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function recordMeasuredAtSeconds(measuredAt: string) {
  const milliseconds = Date.parse(measuredAt);
  return Number.isFinite(milliseconds) ? Math.floor(milliseconds / 1000) : 0;
}

function toSavePayload(
  record: FaceMeasurementRecord | PdMeasurementRecord,
  saveMode: SaveCustomerMeasurementPayload['save_mode'],
): SaveCustomerMeasurementPayload | null {
  const measuredAt = recordMeasuredAtSeconds(record.measuredAt);
  if (!isUnixSeconds(measuredAt)) return null;

  if ('onlinePdMm' in record) {
    if (!isPdValue(record.onlinePdMm)) return null;
    return {
      save_mode: saveMode,
      measurement_type: 'pd',
      pd: record.onlinePdMm,
      measured_at: measuredAt,
    };
  }

  const recommendedSizes = normalizeCustomerSizes(record.recommendedFrameSizes);
  if (!isFaceWidthValue(record.faceWidthMm) || recommendedSizes.length === 0) return null;
  return {
    save_mode: saveMode,
    measurement_type: 'face_width',
    face_width: record.faceWidthMm,
    recommended_sizes: recommendedSizes,
    measured_at: measuredAt,
  };
}

function cacheGuestMeasurement(
  storage: MeasurementStorage | null,
  payload: SaveCustomerMeasurementPayload,
) {
  if (!storage) return;
  if (payload.measurement_type === 'pd') {
    writeJson(storage, GUEST_PD_MEASUREMENT_STORAGE_KEY, {
      pd: payload.pd,
      measured_at: payload.measured_at,
    } satisfies CachedPdMeasurement);
    return;
  }

  writeJson(storage, GUEST_FACE_WIDTH_MEASUREMENT_STORAGE_KEY, {
    face_width: payload.face_width,
    recommended_sizes: payload.recommended_sizes,
    measured_at: payload.measured_at,
  } satisfies CachedFaceWidthMeasurement);
}

export async function synchronizeCustomerMeasurement({
  isAuthenticated,
  customerId,
  storage,
  api = DEFAULT_API,
}: {
  isAuthenticated: boolean;
  customerId: string | null;
  storage: MeasurementStorage | null;
  api?: MeasurementPersistenceApi;
}) {
  const generation = ++synchronizationGeneration;
  const synchronizationRevision = persistenceMutationRevision;
  const initialCache = storage
    ? readCachedMeasurementState(storage)
    : { pd: null, faceWidth: null, feedback: null };
  replaceSessionFromCache(initialCache);
  if (!isAuthenticated) return;

  const jobs: Array<{
    type: 'pd' | 'face_width' | 'feedback';
    run: () => Promise<{ code: number }>;
  }> = [];
  if (initialCache.pd) {
    jobs.push({
      type: 'pd',
      run: () =>
        api.saveMeasurement({
          save_mode: 'guest_merge',
          measurement_type: 'pd',
          pd: initialCache.pd!.pd,
          measured_at: initialCache.pd!.measured_at,
        }),
    });
  }
  if (initialCache.faceWidth) {
    jobs.push({
      type: 'face_width',
      run: () =>
        api.saveMeasurement({
          save_mode: 'guest_merge',
          measurement_type: 'face_width',
          face_width: initialCache.faceWidth!.face_width,
          recommended_sizes: initialCache.faceWidth!.recommended_sizes,
          measured_at: initialCache.faceWidth!.measured_at,
        }),
    });
  }
  if (
    initialCache.feedback?.pending &&
    (initialCache.feedback.owner_customer_id === null ||
      initialCache.feedback.owner_customer_id === customerId)
  ) {
    jobs.push({
      type: 'feedback',
      run: () => api.saveFeedback(initialCache.feedback!.feedback),
    });
  }

  const results = await Promise.allSettled(jobs.map((job) => job.run()));
  results.forEach((result, index) => {
    const code = result.status === 'fulfilled' ? result.value.code : getResponseCode(result.reason);
    // 409 属于参数冲突，后端合同要求不再重试；其余失败保留本地数据。
    if ((code !== 200 && code !== 409) || !storage) return;
    const type = jobs[index].type;
    if (type === 'pd') {
      removeStorageValue(storage, GUEST_PD_MEASUREMENT_STORAGE_KEY);
    } else if (type === 'face_width') {
      removeStorageValue(storage, GUEST_FACE_WIDTH_MEASUREMENT_STORAGE_KEY);
    } else if (initialCache.feedback) {
      writeJson(storage, MEASUREMENT_FEEDBACK_STORAGE_KEY, {
        ...initialCache.feedback,
        pending: false,
        owner_customer_id: customerId,
      } satisfies CachedMeasurementFeedback);
    }
  });

  const fallbackCache = storage
    ? readCachedMeasurementState(storage)
    : { pd: null, faceWidth: null, feedback: null };
  try {
    const response = await api.fetchMeasurement();
    if (
      response.code === 200 &&
      generation === synchronizationGeneration &&
      synchronizationRevision === persistenceMutationRevision
    ) {
      replaceSessionFromAccount(response.data, fallbackCache);
    }
  } catch (error) {
    logger.warn('[MeasurementPersistence] Failed to refresh account measurement', error);
  }
}

export async function persistMeasurementRecord({
  record,
  isAuthenticated,
  storage,
  api = DEFAULT_API,
}: {
  record: FaceMeasurementRecord | PdMeasurementRecord;
  isAuthenticated: boolean;
  storage: MeasurementStorage | null;
  api?: MeasurementPersistenceApi;
}): Promise<{ saved: boolean; message?: string }> {
  const payload = toSavePayload(record, isAuthenticated ? 'direct' : 'guest_merge');
  if (!payload) return { saved: false };
  persistenceMutationRevision += 1;

  if (!isAuthenticated) {
    cacheGuestMeasurement(storage, payload);
    if ('onlinePdMm' in record) {
      savePdMeasurementSession(record);
    } else {
      saveFaceMeasurementSession(record);
    }
    return { saved: true };
  }

  try {
    const response = await api.saveMeasurement(payload);
    if (response.code !== 200) return { saved: false, message: response.msg };

    const cache = storage
      ? readCachedMeasurementState(storage)
      : { pd: null, faceWidth: null, feedback: null };
    replaceSessionFromAccount(response.data.measurement, cache, true);
    if ('onlinePdMm' in record) {
      savePdMeasurementSession(record);
    } else {
      saveFaceMeasurementSession(record);
    }
    return { saved: true };
  } catch (error) {
    logger.warn('[MeasurementPersistence] Failed to save measurement', error);
    return { saved: false };
  }
}

export async function persistMeasurementFeedback({
  feedback,
  isAuthenticated,
  customerId,
  storage,
  api = DEFAULT_API,
}: {
  feedback: 'like' | 'dislike';
  isAuthenticated: boolean;
  customerId: string | null;
  storage: MeasurementStorage | null;
  api?: MeasurementPersistenceApi;
}): Promise<{ saved: boolean; message?: string }> {
  const apiFeedback: CustomerMeasurementFeedback = feedback === 'like' ? 'positive' : 'negative';
  persistenceMutationRevision += 1;
  const cachedFeedback: CachedMeasurementFeedback = {
    feedback: apiFeedback,
    pending: true,
    owner_customer_id: isAuthenticated ? customerId : null,
  };
  if (storage) {
    writeJson(storage, MEASUREMENT_FEEDBACK_STORAGE_KEY, cachedFeedback);
  }
  setMeasurementFeedbackSubmitted(true);
  if (!isAuthenticated) return { saved: true };

  try {
    const response = await api.saveFeedback(apiFeedback);
    if (response.code !== 200) return { saved: false, message: response.msg };
    if (storage) {
      writeJson(storage, MEASUREMENT_FEEDBACK_STORAGE_KEY, {
        ...cachedFeedback,
        pending: false,
      } satisfies CachedMeasurementFeedback);
    }
    return { saved: true };
  } catch (error) {
    logger.warn('[MeasurementPersistence] Failed to save measurement feedback', error);
    return { saved: false };
  }
}

const activeSynchronizations = new Map<string, Promise<void>>();

function synchronizeOnce(key: string, input: Parameters<typeof synchronizeCustomerMeasurement>[0]) {
  const activeSynchronization = activeSynchronizations.get(key);
  if (activeSynchronization) return activeSynchronization;

  const promise = synchronizeCustomerMeasurement(input).finally(() => {
    if (activeSynchronizations.get(key) === promise) activeSynchronizations.delete(key);
  });
  activeSynchronizations.set(key, promise);
  return promise;
}

export function useMeasurementPersistence() {
  const token = useStore((state) => state.token);
  const clientTokenReady = useStore((state) => state.clientTokenReady);
  const customerIdValue = useStore((state) => state.userInfo?.id);
  const customerId = customerIdValue == null ? null : String(customerIdValue);
  const isAuthenticated = Boolean(token);
  const feedbackSubmitted = useMeasurementFeedbackSubmitted();

  useEffect(() => {
    if (!clientTokenReady) return;
    const key = isAuthenticated ? `account:${customerId ?? 'unknown'}` : 'guest';
    void synchronizeOnce(key, {
      isAuthenticated,
      customerId,
      storage: getBrowserStorage(),
    }).catch((error) => {
      logger.warn('[MeasurementPersistence] Failed to synchronize measurement', error);
    });
  }, [clientTokenReady, customerId, isAuthenticated, token]);

  const saveMeasurement = useCallback(
    (record: FaceMeasurementRecord | PdMeasurementRecord) =>
      persistMeasurementRecord({
        record,
        isAuthenticated,
        storage: getBrowserStorage(),
      }),
    [isAuthenticated],
  );
  const saveFeedback = useCallback(
    (feedback: 'like' | 'dislike') =>
      persistMeasurementFeedback({
        feedback,
        isAuthenticated,
        customerId,
        storage: getBrowserStorage(),
      }),
    [customerId, isAuthenticated],
  );

  return { feedbackSubmitted, saveFeedback, saveMeasurement };
}
